import React from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { Modal, Panel, PanelGroup, designSystem, reactHookForm } from '@yola/ws-ui';
import { hdrm, utils } from '@yola/ws-sdk';
import { constants as subscriptionConstants } from '@yola/subscription-manager-js';
import isFeatureAvailable from 'src/js/modules/upsell/verifiers/is-feature-available';
import fieldNames from '../constants/field-names';
import { MAX_FOLDER_NAME_CHARS } from '../constants';

const {
  hostingPackageFeatureNames: { AI_LOCALIZATION },
} = subscriptionConstants;

const {
  ControlGroup,
  FieldStatus,
  InputGroupField,
  TreeSelectField,
  Stack,
  Icon,
  Tag,
  Box,
  RadioButtonGroupField,
  Paragraph,
  Link,
  CheckboxField,
  DialogHeader,
  DialogHeaderTitle,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  NotificationMessage,
} = designSystem;

const { useFormState } = reactHookForm;

const ATTRIBUTES = {
  [hdrm.constants.attributes.DISABLE_BLUR_ATTR]: true,
};

const DIALOG_WIDTH = 334;
const FLOATING_ELEMENT_SELECTOR = '.ws-ui-tree-select__list';

const requiredValidation = (value) => value.length > 0;
const initialExpandedPageItemIdsDefault = [];

function TranslatePageDialog({
  pageItems,
  languageItems,
  overrideExistingPagesOptions,
  captions,
  control,
  showOverrideExistingPages,
  initialExpandedPageItemIds = initialExpandedPageItemIdsDefault,
  validatePages,
  onTranslateWithAiClick,
  onTranslationGuidelinesClick,
  onSubmit,
  onCancel,
}) {
  const { errors } = useFormState({ control });

  return (
    <Modal
      width={DIALOG_WIDTH}
      height="auto"
      centered
      resizable={false}
      draggable={false}
      attributes={ATTRIBUTES}
      handleCancel={onCancel}
      onOverlayClick={utils.noop}
      onPressEsc={onCancel}
      onPressEnter={onSubmit}
      overlay="visible"
      isContainerScrollable
      offsetX={0}
      offsetY={0}
      className="ws-translate-page-dialog"
      floatingModalElementSelector={FLOATING_ELEMENT_SELECTOR}
      fullscreen={bowser.mobile}
    >
      <PanelGroup height="100%">
        <Panel corners="squared" align="middle" className="ws-translate-page-dialog__header">
          <DialogHeader>
            <DialogHeaderTitle>{captions.title}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel className="ws-translate-page-dialog__body" height="100%" scrollable>
          <Box
            paddingTop="spacing-s"
            paddingBottom="spacing-s"
            paddingLeft="spacing-m"
            paddingRight="spacing-m"
          >
            <Stack gap="spacing-xs">
              {errors[fieldNames.TRANSLATE_PAGE_IDS]?.type === 'validate' && (
                <NotificationMessage
                  appearance="warning"
                  iconGlyph="info"
                  title={<Paragraph>{captions.notificationTitle}</Paragraph>}
                />
              )}
              <ControlGroup title={`${captions.fields.pages.label}:`}>
                <TreeSelectField
                  control={control}
                  name={fieldNames.TRANSLATE_PAGE_IDS}
                  items={pageItems}
                  size="small"
                  initialExpandedIds={initialExpandedPageItemIds}
                  placeholder={captions.fields.pages.placeholder}
                  searchPlaceholder={captions.fields.pages.searchPlaceholder}
                  selectAllLabel={captions.fields.pages.selectAllLabel}
                  noResultsText={captions.fields.pages.noResultsText}
                  getSelectionLabel={captions.fields.pages.getSelectionLabel}
                  rules={{
                    required: requiredValidation,
                    validate: validatePages,
                  }}
                />
              </ControlGroup>
              <Stack gap="spacing-3-xs">
                <ControlGroup title={`${captions.fields.languages.label}:`}>
                  <TreeSelectField
                    control={control}
                    name={fieldNames.TRANSLATE_LOCALES}
                    items={languageItems}
                    size="small"
                    withSelectAll={false}
                    placeholder={captions.fields.languages.placeholder}
                    searchPlaceholder={captions.fields.languages.searchPlaceholder}
                    noResultsText={captions.fields.languages.noResultsText}
                    getSelectionLabel={captions.fields.languages.getSelectionLabel}
                    rules={{
                      required: requiredValidation,
                    }}
                  />
                </ControlGroup>
                <div>
                  <InputGroupField
                    control={control}
                    name={fieldNames.TARGET_FOLDER_NAME}
                    size="small"
                    autoComplete="off"
                    placeholder={captions.fields.targetFolderName.placeholder}
                    maxLength={MAX_FOLDER_NAME_CHARS}
                    rules={{
                      maxLength: {
                        value: MAX_FOLDER_NAME_CHARS,
                        message: captions.fields.targetFolderName.maxLengthError,
                      },
                    }}
                  />
                  <FieldStatus
                    appearance="default"
                    text={captions.fields.targetFolderName.description}
                  />
                </div>
              </Stack>
              {showOverrideExistingPages && (
                <ControlGroup
                  title={captions.fields.overrideExistingPages.title}
                  description={captions.fields.overrideExistingPages.description}
                >
                  <RadioButtonGroupField
                    name={fieldNames.EXISTING_PAGE_RESOLVING}
                    control={control}
                    options={overrideExistingPagesOptions}
                  />
                </ControlGroup>
              )}
              <Stack gap="spacing-3-xs">
                <CheckboxField
                  id={fieldNames.TRANSLATE_AUTO_TRANS}
                  name={fieldNames.TRANSLATE_AUTO_TRANS}
                  control={control}
                  value={fieldNames.TRANSLATE_AUTO_TRANS}
                  size="medium"
                  isLabelBold
                  onChange={onTranslateWithAiClick}
                  label={
                    <span className="ws-translate-page-dialog__checkbox-label">
                      <span>{captions.fields.autoTranslate.label}</span>
                      <Icon glyph="ai" type="accent" />
                      {!isFeatureAvailable(AI_LOCALIZATION) && (
                        <Tag size="small" appearance="cta-secondary" label="PRO" />
                      )}
                    </span>
                  }
                  description={
                    <span>
                      {captions.fields.autoTranslate.guidelinesDescription}
                      {/* eslint-disable-next-line yola/jsx-a11y/anchor-is-valid */}
                      <Link
                        href="/"
                        isUnderlined
                        onClick={(e) => {
                          e.preventDefault();
                          onTranslationGuidelinesClick();
                        }}
                      >
                        {captions.fields.autoTranslate.guidelinesLinkText}
                      </Link>
                    </span>
                  }
                />
              </Stack>
            </Stack>
          </Box>
        </Panel>
        <Panel className="ws-translate-page-dialog__footer">
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submit}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancel}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
}

TranslatePageDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  languageItems: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  pageItems: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  overrideExistingPagesOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  captions: PropTypes.shape({
    title: PropTypes.string,
    fields: PropTypes.shape({
      pages: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        searchPlaceholder: PropTypes.string,
        selectAllLabel: PropTypes.string,
        noResultsText: PropTypes.string,
        getSelectionLabel: PropTypes.func,
      }),
      languages: PropTypes.shape({
        label: PropTypes.string,
        placeholder: PropTypes.string,
        searchPlaceholder: PropTypes.string,
        noResultsText: PropTypes.string,
        getSelectionLabel: PropTypes.func,
        notificationDescription: PropTypes.string,
        notificationPath: PropTypes.string,
      }),
      overrideExistingPages: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        radioButton1: PropTypes.string,
        radioButton2: PropTypes.string,
      }),
      autoTranslate: PropTypes.shape({
        label: PropTypes.string,
        guidelinesDescription: PropTypes.string,
        guidelinesLinkText: PropTypes.string,
      }),
      targetFolderName: PropTypes.shape({
        placeholder: PropTypes.string,
        description: PropTypes.string,
        maxLengthError: PropTypes.string,
      }),
    }),
    notificationTitle: PropTypes.string,
    submit: PropTypes.string,
    cancel: PropTypes.string,
  }).isRequired,
  control: PropTypes.shape({}).isRequired,
  validatePages: PropTypes.func.isRequired,
  showOverrideExistingPages: PropTypes.bool.isRequired,
  onTranslateWithAiClick: PropTypes.func.isRequired,
  onTranslationGuidelinesClick: PropTypes.func.isRequired,
  initialExpandedPageItemIds: PropTypes.arrayOf(PropTypes.string),
};

export default TranslatePageDialog;
